import { useEffect, useState } from 'react';
import Card from '../../components/Card.jsx';
import DataTable from '../../components/DataTable.jsx';
import Chip from '../../components/Chip.jsx';
import { msaApi } from '../../lib/apiClient.js';

const EXCHANGES = ['ALL', 'NGX', 'NSE', 'JSE', 'GSE', 'EGX', 'BRVM'];
const SECTORS = ['ALL', 'Financials', 'Telecommunications', 'Materials', 'Technology'];
const PAGE_SIZE = 50;

const DEFAULT_FILTERS = {
  exchange: 'ALL',
  sector: 'ALL',
  maxPe: '',
  minDivYield: '',
  minMarketCap: '',
  currency: 'ALL',
};

export default function Screener() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ currencies: [] });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    msaApi
      .profiles({
        exchange: filters.exchange,
        sector: filters.sector,
        maxPe: filters.maxPe,
        minDivYield: filters.minDivYield,
        minMarketCap: filters.currency === 'ALL' ? '' : filters.minMarketCap,
        currency: filters.currency,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      })
      .then((data) => {
        if (!alive) return;
        const items = data.items || [];
        setRows(items);
        setTotal(Number.isFinite(data.total) ? data.total : items.length);
        setFacets(data.facets || { currencies: [] });
      })
      .catch((err) => { if (alive) { setRows([]); setTotal(0); setError(err.message || 'Fundamentals unavailable.'); } })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  function setFilter(key, value) {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <Card>
        <h1 className="heading" style={{ fontSize: 22 }}>
          Stock Screener
        </h1>
        <p className="aq-module-desc">MSA directory · six supported exchanges · live price &amp; validated available fundamentals (P/E and div yield are shown only when internally consistent)</p>
      </Card>

      <Card className="aq-filters">
        {error && <p role="alert">{error}</p>}
        <p className="aq-module-desc">Missing values do not pass numeric filters. These are current snapshots, not historical point-in-time fundamentals.</p>
        <div className="aq-filters-grid">
          <label className="aq-field">
            <span>Exchange</span>
            <select value={filters.exchange} onChange={(e) => setFilter('exchange', e.target.value)}>
              {EXCHANGES.map((ex) => (
                <option key={ex} value={ex}>
                  {ex}
                </option>
              ))}
            </select>
          </label>
          <label className="aq-field">
            <span>Sector</span>
            <select value={filters.sector} onChange={(e) => setFilter('sector', e.target.value)}>
              {SECTORS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="aq-field">
            <span>Max P/E</span>
            <input
              type="number"
              placeholder="e.g. 15"
              value={filters.maxPe}
              onChange={(e) => setFilter('maxPe', e.target.value)}
            />
          </label>
          <label className="aq-field">
            <span>Min dividend yield %</span>
            <input
              type="number"
              placeholder="e.g. 4"
              value={filters.minDivYield}
              onChange={(e) => setFilter('minDivYield', e.target.value)}
            />
          </label>
          <label className="aq-field">
            <span>Min market cap (listing-currency M)</span>
            <input
              type="number"
              disabled={filters.currency === 'ALL'}
              placeholder="e.g. 1000"
              value={filters.minMarketCap}
              onChange={(e) => setFilter('minMarketCap', e.target.value)}
            />
          </label>
          <label className="aq-field"><span>Market-cap currency</span><select value={filters.currency} onChange={(e) => setFilter('currency', e.target.value)}>
            <option value="ALL">Choose currency for cap filter</option>
            {(facets.currencies || []).map((currency) => <option key={currency}>{currency}</option>)}
          </select></label>
          <button className="aq-btn aq-btn--ghost aq-btn--sm" onClick={() => { setFilters(DEFAULT_FILTERS); setPage(1); }}>
            Reset
          </button>
        </div>
      </Card>

      <Card>
        <div className="aq-row-head" style={{ marginBottom: 10 }}>
          <span className="aq-module-desc">{loading ? 'Loading results…' : `${total.toLocaleString()} result${total === 1 ? '' : 's'} · page ${page} of ${pageCount}`}</span>
          <div className="aq-seg" aria-label="Screener pagination">
            <button className="aq-seg-btn" disabled={loading || page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</button>
            <button className="aq-seg-btn" disabled={loading || page >= pageCount} onClick={() => setPage((value) => Math.min(pageCount, value + 1))}>Next</button>
          </div>
        </div>
        <DataTable
          loading={loading}
          emptyLabel="No stocks match these filters."
          columns={[
            { key: 'symbol', header: 'Symbol' },
            { key: 'name', header: 'Company' },
            { key: 'exchange', header: 'Exchange', render: (r) => <Chip tone="info">{r.exchange}</Chip> },
            { key: 'sector', header: 'Sector', render: (r) => <span title={r.sectorRaw || r.sector}>{r.sector}</span> },
            { key: 'price', header: 'Price', numeric: true, render: (r) => (r.price != null ? r.price.toFixed(2) : '—') },
            {
              key: 'pe',
              header: 'P/E',
              numeric: true,
              render: (r) => (r.pe != null ? r.pe.toFixed(1) : '—'),
            },
            {
              key: 'divYield',
              header: 'Div yield',
              numeric: true,
              render: (r) => (r.divYield != null ? `${r.divYield.toFixed(1)}%` : '—'),
            },
            {
              key: 'marketCap',
              header: 'Market cap',
              numeric: true,
              render: (r) => (r.marketCap != null ? `${r.marketCapCurrency || ''} ${(r.marketCap / 1_000_000_000).toFixed(2)}B` : 'Not supplied'),
            },
          ]}
          rows={rows}
        />
      </Card>
    </>
  );
}
